import { useState, useCallback, useMemo } from 'react';
import type { GaugeItem, GaugeProgress } from '../../types/driver';
import { formatRelativeTime } from '../../services/mockData';
import { ShapeAwareList, ShapeAwareItem } from '../ShapeAwareList';
import { LedPillToggle } from '../LedPillToggle';
import './gauge-check.css';

interface GaugeCheckProps {
  items: GaugeItem[];
  onToggleItem?: (itemId: string, completed: boolean) => void;
  onSkipItem?: (itemId: string) => void;
  /** When true, header is rendered externally via TasksHeader */
  externalHeader?: boolean;
}

type ViewMode = 'tasks' | 'documents';

/**
 * Props for the standalone TasksHeader component
 */
interface TasksHeaderProps {
  openCount: number;
  activeView: ViewMode;
  onViewToggle: (position: 'left' | 'right') => void;
}

/**
 * TasksHeader - Standalone header for VortexLayout's headerRight slot
 *
 * Renders the tasks panel header content (title, count, Tasks/Documents toggle)
 * without any vortex-aware positioning - VortexLayout handles that.
 */
export function TasksHeader({
  openCount,
  activeView,
  onViewToggle,
}: TasksHeaderProps): JSX.Element {
  return (
    <div className="gauge-header gauge-header--standalone">
      <div className="gauge-header-text">
        <h3 className="gauge-title">TASKS</h3>
        <p className="gauge-subtitle">{openCount} open</p>
      </div>
      <div className="gauge-header-toggle">
        <span className="toggle-label toggle-label--left">Tasks</span>
        <LedPillToggle
          leftLabel="Tasks"
          rightLabel="Documents"
          activePosition={activeView === 'tasks' ? 'left' : 'right'}
          onToggle={onViewToggle}
          size="small"
        />
        <span className="toggle-label toggle-label--right">Documents</span>
      </div>
    </div>
  );
}

function CheckIcon(): JSX.Element {
  return (
    <svg
      className="check-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function calculateProgress(items: GaugeItem[]): GaugeProgress {
  const total = items.length;
  const completed = items.filter((i) => i.status === 'completed').length;
  const skipped = items.filter((i) => i.status === 'skipped').length;
  const overdue = items.filter((i) => i.status === 'overdue').length;
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
  return { total, completed, skipped, overdue, percentage };
}

export function ProgressRing({
  progress,
  size = 80,
  strokeWidth = 6,
}: {
  progress: GaugeProgress;
  size?: number;
  strokeWidth?: number;
}): JSX.Element {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress.percentage / 100) * circumference;
  const viewBoxValue = '0 0 ' + size + ' ' + size;
  const rotateValue = 'rotate(-90 ' + (size / 2) + ' ' + (size / 2) + ')';

  return (
    <div className="progress-ring-container">
      <svg
        className="progress-ring"
        width={size}
        height={size}
        viewBox={viewBoxValue}
        aria-hidden="true"
      >
        <circle
          className="progress-ring-bg"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
        />
        <circle
          className="progress-ring-progress"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={rotateValue}
        />
      </svg>
      <div className="progress-ring-content">
        <span className="progress-ring-value">{progress.percentage}%</span>
        <span className="progress-ring-label">Complete</span>
      </div>
    </div>
  );
}

function TaskItem({
  item,
  onToggle,
}: {
  item: GaugeItem;
  onToggle?: (completed: boolean) => void;
}): JSX.Element {
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  const handleToggle = useCallback(() => {
    setIsAnimatingOut(true);
    setTimeout(() => {
      onToggle?.(true);
    }, 300);
  }, [onToggle]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleToggle();
      }
    },
    [handleToggle]
  );

  const rowClasses = [
    'gauge-item',
    'gauge-item--current',
    item.isRequired ? 'gauge-item--required' : '',
    isAnimatingOut ? 'gauge-item--completing' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const ariaLabelValue = item.title + (item.isRequired ? ' (required)' : '');

  return (
    <div
      className={rowClasses}
      onClick={handleToggle}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="checkbox"
      aria-checked={false}
      aria-label={ariaLabelValue}
    >
      <div className="gauge-item-checkbox">
        {isAnimatingOut && <CheckIcon />}
      </div>
      <div className="gauge-item-content">
        <span className="gauge-item-title">
          {item.title}
          {item.isRequired && <span className="gauge-item-required">*</span>}
        </span>
        {item.description && (
          <span className="gauge-item-description">{item.description}</span>
        )}
      </div>
      <div className="gauge-item-right">
        <span className="gauge-item-author">{item.createdBy}</span>
        <span className="gauge-item-time">{formatRelativeTime(item.createdAt)}</span>
      </div>
    </div>
  );
}

function TasksEmptyState(): JSX.Element {
  return (
    <div className="gauge-empty">
      <span className="gauge-empty-icon">&#10003;</span>
      <span className="gauge-empty-text">All tasks completed!</span>
    </div>
  );
}

/**
 * Props for GaugeCheckBody (header-less version)
 */
interface GaugeCheckBodyProps {
  items: GaugeItem[];
  onToggleItem?: (itemId: string, completed: boolean) => void;
  activeView: ViewMode;
  className?: string;
}

/**
 * GaugeCheckBody - Body-only version for use with external header
 *
 * Renders the task list and documents placeholder without the header.
 * Used when TasksHeader is rendered in VortexLayout's headerRight slot.
 */
export function GaugeCheckBody({
  items,
  onToggleItem,
  activeView,
  className = '',
}: GaugeCheckBodyProps): JSX.Element {
  const progress = useMemo(() => calculateProgress(items), [items]);

  const currentItems = useMemo(
    () => items.filter((item) => item.status === 'pending' || item.status === 'overdue'),
    [items]
  );

  return (
    <section className={`gauge-check gauge-check--no-header ${className}`} aria-label="Tasks">
      {activeView === 'tasks' && (
        <ShapeAwareList panelSide="right" className="gauge-list">
          {currentItems.length === 0 ? (
            <TasksEmptyState />
          ) : (
            currentItems.map((item) => (
              <ShapeAwareItem key={item.id}>
                <TaskItem
                  item={item}
                  onToggle={(completed) => onToggleItem?.(item.id, completed)}
                />
              </ShapeAwareItem>
            ))
          )}
        </ShapeAwareList>
      )}

      {activeView === 'documents' && (
        <div className="documents-placeholder">
          <p>Documents coming soon</p>
        </div>
      )}

      {activeView === 'tasks' && progress.overdue > 0 && (
        <div className="gauge-footer">
          <span className="gauge-overdue-warning">
            {progress.overdue} overdue item{progress.overdue > 1 ? 's' : ''}
          </span>
        </div>
      )}
    </section>
  );
}

export function GaugeCheck({
  items,
  onToggleItem,
  externalHeader = false,
}: GaugeCheckProps): JSX.Element {
  const [activeView, setActiveView] = useState<ViewMode>('tasks');

  const progress = useMemo(() => calculateProgress(items), [items]);

  const currentItems = useMemo(
    () => items.filter((item) => item.status === 'pending' || item.status === 'overdue'),
    [items]
  );

  const handleViewToggle = useCallback((position: 'left' | 'right') => {
    setActiveView(position === 'left' ? 'tasks' : 'documents');
  }, []);

  // When using external header, render body only
  if (externalHeader) {
    return (
      <GaugeCheckBody
        items={items}
        onToggleItem={onToggleItem}
        activeView={activeView}
        className=""
      />
    );
  }

  return (
    <section className="gauge-check" aria-label="Tasks">
      <div className="gauge-header">
        <div className="gauge-header-text">
          <h3 className="gauge-title">TASKS</h3>
          <p className="gauge-subtitle">{currentItems.length} open</p>
        </div>
        <div className="gauge-header-toggle">
          <span className="toggle-label toggle-label--left">Tasks</span>
          <LedPillToggle
            leftLabel="Tasks"
            rightLabel="Documents"
            activePosition={activeView === 'tasks' ? 'left' : 'right'}
            onToggle={handleViewToggle}
            size="small"
          />
          <span className="toggle-label toggle-label--right">Documents</span>
        </div>
      </div>

      {activeView === 'tasks' && (
        <ShapeAwareList panelSide="right" className="gauge-list">
          {currentItems.length === 0 ? (
            <TasksEmptyState />
          ) : (
            currentItems.map((item) => (
              <ShapeAwareItem key={item.id}>
                <TaskItem
                  item={item}
                  onToggle={(completed) => onToggleItem?.(item.id, completed)}
                />
              </ShapeAwareItem>
            ))
          )}
        </ShapeAwareList>
      )}

      {activeView === 'documents' && (
        <div className="documents-placeholder">
          <p>Documents coming soon</p>
        </div>
      )}

      {activeView === 'tasks' && progress.overdue > 0 && (
        <div className="gauge-footer">
          <span className="gauge-overdue-warning">
            {progress.overdue} overdue item{progress.overdue > 1 ? 's' : ''}
          </span>
        </div>
      )}
    </section>
  );
}

export default GaugeCheck;
